import React from "react";
import { Eye, Lock, GitBranch } from "lucide-react";
import { Tile, Badge } from "../Primitives";
import { LayerChip, StageTrack, SOURCE, when, num } from "../pipeline/Layer";
import { kindOf } from "./CatalogRail";
import { cn } from "../../lib/utils";

function Columns({ cols }) {
  return (
    <Tile testid="table-schema" title={`Columns (${cols.length})`} dense>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
          <th className="py-2 px-3 text-left">Column</th><th className="py-2 px-3 text-left">Type</th><th className="py-2 px-3 text-center">Personal</th><th className="py-2 px-3 text-left">Description</th></tr></thead>
        <tbody className="font-mono-tabular">{cols.map(c => (
          <tr key={c.key} className="border-b border-border last:border-b-0" data-testid={`col-${c.key}`}>
            <td className="py-2 px-3 font-semibold text-fg">{c.key}</td><td className="py-2 px-3 text-info">{c.type}</td>
            <td className="py-2 px-3 text-center">{c.pii ? <Badge status="warning">masked</Badge> : "—"}</td>
            <td className="py-2 px-3 font-sans text-[11.5px] text-muted">{c.description}</td></tr>))}</tbody></table></div>
      {!cols.length && <div className="p-4 text-[12px] text-muted">Columns appear once rows are promoted into this table.</div>}
    </Tile>
  );
}

function Sources({ t }) {
  return (
    <Tile testid="table-sources" title="Came from Bronze review" action={<GitBranch size={12} className="text-primary" />}>
      <div className="space-y-1">{t.sources.slice().reverse().map(s => (
        <div key={`${s.id}-${s.at}`} className="flex flex-wrap items-center gap-2 text-[12px]"><LayerChip layer="Bronze" /><span className="flex-1 truncate">{s.filename}</span>
          <span className="text-muted font-mono-tabular text-[11px]">{SOURCE[s.source] || "Upload"} · {num(s.rows)} rows · {when(s.at)}</span></div>))}</div>
    </Tile>
  );
}

// Right panel of the Explorer: what the table is, where it fills from, what it powers, its columns and a real-row drill-down.
export default function CatalogDetail({ t, isIT, onOpen }) {
  const [k, tone] = kindOf(t);
  const stage = t.layer.toLowerCase();
  const canOpen = t.kind && t.kind !== "empty";
  return (
    <div className="space-y-4">
      <Tile testid="table-detail" title={<span className="font-mono-tabular text-[13px] text-fg normal-case tracking-normal">{t.display}</span>}
            action={<div className="flex items-center gap-2"><LayerChip layer={t.layer} testid="detail-layer" /><Badge status="neutral" testid="detail-rows">{num(t.rows_held)} rows</Badge></div>}>
        <div className="flex flex-wrap items-center gap-3 mb-3"><StageTrack stage={stage} testid="detail-stage" /><span className={cn("text-[12px] font-semibold", tone)} data-testid="detail-kind">{k}</span>
          {t.quality != null && <span className="text-[12px] text-muted">Quality <b className="text-fg font-mono-tabular">{t.quality}</b></span>}</div>
        <p className="text-[12.5px] text-muted">{t.description}</p>
        <div className="text-[12px] mt-2" data-testid="detail-source">{t.fills_from || t.origin?.source || t.window?.label || "—"}{t.updated_at ? <span className="text-muted"> · updated {when(t.updated_at)}</span> : null}</div>
        <hr className="my-3 border-border" />
        <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">Powers these dashboards / models</div>
        <div className="flex flex-wrap gap-2" data-testid="detail-powers">{t.powers.length ? t.powers.map(p => <Badge key={p} status="info">{p}</Badge>) : <span className="text-[12px] text-muted">Model Readiness and Gold facts once certified</span>}</div>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button data-testid="detail-open-rows" disabled={!canOpen} onClick={onOpen} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm text-[12.5px] font-medium bg-primary text-primary-fg hover:opacity-90 disabled:opacity-45 disabled:cursor-not-allowed transition-opacity">
            <Eye size={13} />{canOpen ? "Open rows" : "No rows yet"}</button>
          {!isIT && t.columns.some(c => c.pii) && <span className="text-[11.5px] text-warning inline-flex items-center gap-1"><Lock size={11} />Personal details are masked for you</span>}
          <span className="text-[11.5px] text-muted">Read-only · rows are cleaned and promoted in the Data Management Console</span>
        </div>
      </Tile>
      {t.sources?.length > 0 && <Sources t={t} />}
      <Columns cols={t.columns} />
    </div>
  );
}
